function loadScriptOnce(src, globalName) {
  if (window[globalName]) return Promise.resolve(window[globalName]);

  const existing = document.querySelector(`script[data-partbot-src="${src}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window[globalName]), { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.partbotSrc = src;
    script.onload = () => resolve(window[globalName]);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function normalizeSearchHost(host, fallback) {
  const value = host || fallback;
  return value.startsWith("http") ? value : `https://${value}`;
}

function sortedRequestOnPrimaryIndex(request, primaryIndexName, { dropSort = false } = {}) {
  if (!request || !primaryIndexName) return request;

  const requestIndexName = request.indexName || request.indexUid || "";
  if (requestIndexName === primaryIndexName && dropSort && request.params?.sort) {
    const { sort, ...params } = request.params;
    return {
      ...request,
      params,
    };
  }

  const sortPrefix = `${primaryIndexName}:`;
  if (!requestIndexName.startsWith(sortPrefix)) return request;

  const sortValue = requestIndexName.slice(sortPrefix.length);
  return {
    ...request,
    indexName: primaryIndexName,
    indexUid: request.indexUid ? primaryIndexName : request.indexUid,
    params: {
      ...(request.params || {}),
      ...(dropSort || !sortValue ? {} : { sort: [sortValue] }),
    },
  };
}

function sortedRequestsOnPrimaryIndex(requests, primaryIndexName, options = {}) {
  if (!Array.isArray(requests)) return requests;
  return requests.map((request) => sortedRequestOnPrimaryIndex(request, primaryIndexName, options));
}

function isUnsupportedSortError(error) {
  const message = String(error?.message || error || "");
  return message.includes("is not sortable") || message.includes("invalid_search_sort");
}


function resolveInitialSortIndex(searchIndexName, preferredSort) {
  const sortParam = resolveSortParam(preferredSort);
  return sortParam ? `${searchIndexName}:${sortParam}` : searchIndexName;
}

function resolveSortParam(preferredSort) {
  const sort = String(preferredSort || "").trim().toLowerCase();
  if (!sort || sort === "manual" || sort === "relevance") return "";

  if (sort.includes(":")) {
    const parts = sort.split(":");
    return parts.slice(-2).join(":");
  }

  if (sort === "best-selling" || sort === "best_selling") return "quantity_sold:desc";
  if (sort === "price-ascending" || sort === "price_ascending") return "price_from:asc";
  if (sort === "price-descending" || sort === "price_descending") return "price_from:desc";
  if (sort === "title-ascending" || sort === "title_ascending") return "title:asc";
  if (sort === "title-descending" || sort === "title_descending") return "title:desc";

  return "";
}


async function fetchSearchConfig(apiHost, apiKey, store) {
  const response = await fetch(`${apiHost}/v1/vehicles/auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      use_store_vehicles: true,
      ...(store ? { store } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error(`Vehicle auth failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

function normalizeFacetAttribute(facet) {
  if (!facet) return facet;
  if (facet.includes("Brand") || facet.includes("Vendor") || facet === "brand" || facet === "vendor") {
    return "brand";
  }
  if (facet.includes("Product Type") || facet === "product_type") {
    return "product_type";
  }
  if (facet.includes("Kit") || facet.includes("Assembly") || facet === "assembly") {
    return "assembly";
  }
  return facet.includes(".") ? facet : `attributes.${facet}`;
}

function uniq(values) {
  return [...new Set(values.filter(Boolean))];
}

function filterValue(value, { numeric = false } = {}) {
  const stringValue = String(value).trim();
  if (numeric && /^-?\d+(\.\d+)?$/.test(stringValue)) return stringValue;
  return JSON.stringify(stringValue);
}

function quoteFilterAttribute(attribute) {
  const unquotedAttribute = String(attribute || "")
    .trim()
    .replace(/^["']|["']$/g, "");

  if (!unquotedAttribute) return unquotedAttribute;
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(unquotedAttribute)) return unquotedAttribute;
  return JSON.stringify(unquotedAttribute);
}

function equalsFilter(attribute, value, options = {}) {
  return `${quoteFilterAttribute(attribute)} = ${filterValue(value, options)}`;
}

function normalizeLegacyFilterValue(value) {
  const trimmedValue = String(value || "").trim();

  if (/^(true|false)$/i.test(trimmedValue)) return trimmedValue.toLowerCase();
  if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) return trimmedValue;

  if (
    (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) ||
    (trimmedValue.startsWith("'") && trimmedValue.endsWith("'"))
  ) {
    return filterValue(trimmedValue.slice(1, -1).replace(/\\(["'])/g, "$1"));
  }

  return filterValue(trimmedValue);
}

function normalizeLegacySearchFilters(filters) {
  if (!filters) return "";

  return filters.replace(
    /(^|[\s(])((?:"[^"]+"|'[^']+'|[A-Za-z_][A-Za-z0-9_.[\]-]*))\s*:\s*("(?:\\"|[^"])*"|'(?:\\'|[^'])*'|true|false|-?\d+(?:\.\d+)?|[^\s)]+)/gi,
    (match, prefix, attribute, value) =>
      `${prefix}${quoteFilterAttribute(attribute)} = ${normalizeLegacyFilterValue(value)}`,
  );
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function maskKey(key) {
  if (!key) return key;
  if (key.length <= 12) return key;
  return `${key.slice(0, 6)}...${key.slice(-6)}`;
}

class PartbotCollectionSearch extends HTMLElement {
  constructor() {
    super();

    this.collection = this.getAttribute("data-collection");
    this.collectionType = this.getAttribute("data-collection-type");
    this.defaultSortBy = this.getAttribute("data-sortby");
    this.currentSortValue = resolveSortParam(
      new URLSearchParams(window.location.search).get("sort_by") || this.defaultSortBy,
    );
    this.disjunctive = this.getAttribute("data-disjunctive") === "true" ? "OR" : "AND";
    this.productIds = this.getAttribute("data-product-ids");

    this.skus = [];
    this.vehicle_ids = [];
    this.activeVehicleIds = [];
    this.selectedYear = null;
    this.brands = this.getAttribute("data-vendors")
      ? this.getAttribute("data-vendors").split("|")
      : [];
    this.product_types = this.getAttribute("data-product-types")
      ? this.getAttribute("data-product-types")
          .split("|")
          .filter((str) => str.trim() !== "")
      : [];

    this.facets = this.getAttribute("data-facets")
      ? this.getAttribute("data-facets")
          .split(",")
          .filter((str) => str.trim() !== "")
      : [];

    this.filters = this.getAttribute("data-filters");

    this.tags = this.getAttribute("data-tags")
      ? this.getAttribute("data-tags")
          .split("|")
          .filter((str) => str.trim() !== "")
      : [];

    this.showVehicleSearch = this.getAttribute("data-show-vehicle-search") === "true";

    this.partbot_vehicle = localStorage.getItem("partbot_selected_vehicle");
    this.partbot_plate_details = localStorage.getItem("partbot_plate_details");
    this.platesearch_ids = [];

    const hasPlateSearchVehicle =
      this.showVehicleSearch && this.partbot_vehicle && this.partbot_plate_details;

    if (hasPlateSearchVehicle) {
      this.partbot_vehicle = JSON.parse(this.partbot_vehicle);
      this.platesearch_ids = this.partbot_vehicle.vehicle_ids;

      const vehicleDescription = document.querySelector(".js-vehicle-description");

      if (vehicleDescription) vehicleDescription.textContent = this.partbot_vehicle.description;

      const clearButton = document.querySelector(".js-button-clear-vehicle");
      if (clearButton) {
        clearButton.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();

          localStorage.removeItem("partbot_selected_vehicle");
          localStorage.removeItem("partbot_plate_details");
          location.reload();
        });
      }

      const vehicleNotice = document.querySelector(".js-selected-vehicle-notice");

      vehicleNotice.classList.remove("hidden");
      vehicleNotice.classList.add("flex");

      document.querySelector("vehicle-search").classList.add("hidden");

      // add data-vehicle-selected="true" to the vehicle-search container section element
      document
        .querySelector("vehicle-search")
        .closest("section")
        .setAttribute("data-vehicle-selected", "true");
    }

    this.facetFiltersContainer = this.querySelector("#facet-filters");

    const vehicleSearchRoot = document.querySelector("vehicle-search");
    this.apiKey =
      this.getAttribute("data-api-key") || vehicleSearchRoot?.getAttribute("data-api-key");
    this.store =
      this.getAttribute("data-store") ||
      vehicleSearchRoot?.getAttribute("data-store") ||
      window.location.hostname;
    this.apiHost = "https://api.cartbot.io";
    this.searchHost = "https://search.partbot.io";
    this.searchIndexName = null;
    this.debugPrefix = "[PartbotCollectionSearch][SWD]";
    this.unsupportedFacetAttributes = new Set();
    this.baseFilters = "";
    this.manualProductIdsFilter = "";
    this.manualProductIdFilterEnabled = true;
    this.configureWidget = null;
    this.configureWidgetParams = {};
    this.selectedFacetValues = new Map();
    this.facetLabelsByAttribute = new Map();
    this.facetContainersByAttribute = new Map();
    this.facetOptionState = new Map();
    this.facetSyncTimer = null;
    this.facetSyncToken = 0;
    this.hasReceivedSearchResults = false;

    this.hitsContainer = this.querySelector("#partbot-product-grid");

    // hide the hitsContainer if there is a make parameter in the query string
    if (window.location.search.includes("make=")) {
      this.hitsContainer.style.display = "none";
    }

    // URL-derived vehicle search changes are emitted on window only.
    this.lastVehicleSearchEvent = null;
    this.onVehicleSearchChange = this.applyVehicleSearchSelection.bind(this);
    window.addEventListener("vehicle-search-change", this.onVehicleSearchChange);
    document.addEventListener("vehicle-search-change", this.onVehicleSearchChange);
    this.filterAlgoliaHits = this.processHits.bind(this);


    this.initializeSearch().catch((error) => {
      console.error("[PartbotCollectionSearch] Failed to initialize search", error);
    });
  }

  extractUnsupportedFacetAttributes(error) {
    const message = error?.message || String(error || "");
    const match = message.match(/Attributes? `([^`]+)` (?:are|is) not filterable/);
    if (!match) return [];

    return match[1]
      .split(",")
      .map((attribute) => attribute.trim())
      .filter(Boolean);
  }

  sanitizeMeiliQueries(queries) {
    if (!Array.isArray(queries) || this.unsupportedFacetAttributes.size === 0) {
      return queries;
    }

    return queries.map((query) => {
      if (!query || !Array.isArray(query.facets)) {
        return query;
      }

      const facets = query.facets.filter(
        (attribute) => !this.unsupportedFacetAttributes.has(attribute),
      );

      return facets.length === query.facets.length ? query : { ...query, facets };
    });
  }

  sanitizeInstantSearchRequests(requests) {
    if (!Array.isArray(requests) || this.unsupportedFacetAttributes.size === 0) {
      return requests;
    }

    return requests.map((request) => {
      if (!request?.params) return request;

      const sanitizedParams = { ...request.params };

      if (Array.isArray(sanitizedParams.facets)) {
        sanitizedParams.facets = sanitizedParams.facets.filter(
          (attribute) => !this.unsupportedFacetAttributes.has(attribute),
        );
      }

      if (Array.isArray(sanitizedParams.disjunctiveFacets)) {
        sanitizedParams.disjunctiveFacets = sanitizedParams.disjunctiveFacets.filter(
          (attribute) => !this.unsupportedFacetAttributes.has(attribute),
        );
      }

      return { ...request, params: sanitizedParams };
    });
  }

  removeUnsupportedFacets(attributes) {
    if (!attributes.length) return [];

    const removedFacets = [];

    attributes.forEach((attribute) => {
      if (this.unsupportedFacetAttributes.has(attribute)) return;
      this.unsupportedFacetAttributes.add(attribute);

      const matchingFacets = this.facets.filter(
        (facet) => normalizeFacetAttribute(facet) === attribute,
      );
      if (matchingFacets.length === 0) return;

      matchingFacets.forEach((facet) => {
        removedFacets.push(facet);
        this.removeFacetContainer(facet);
      });

      this.facets = this.facets.filter((facet) => normalizeFacetAttribute(facet) !== attribute);
    });

    return removedFacets;
  }

  removeFacetContainer(facet) {
    const facetTitle = facet.replace(/_/g, " ").replace("vendor", "brand");
    const containers = Array.from(this.querySelectorAll(".partbot-filter-container"));
    const container = containers.find(
      (node) => node.querySelector(".partbot-filter-title")?.textContent?.trim() === facetTitle,
    );

    if (container) {
      container.remove();
    }
  }

  unsupportedFacetStorageKey() {
    return `partbot_unsupported_facets:${this.searchIndexName || this.store || "default"}`;
  }

  loadUnsupportedFacetAttributes() {
    try {
      const stored = localStorage.getItem(this.unsupportedFacetStorageKey());
      if (!stored) return;

      JSON.parse(stored).forEach((attribute) => {
        if (attribute) this.unsupportedFacetAttributes.add(attribute);
      });
    } catch (error) {
      console.warn(`${this.debugPrefix} failed to load unsupported facets`, error);
    }
  }

  persistUnsupportedFacetAttributes() {
    try {
      localStorage.setItem(
        this.unsupportedFacetStorageKey(),
        JSON.stringify(Array.from(this.unsupportedFacetAttributes)),
      );
    } catch (error) {
      console.warn(`${this.debugPrefix} failed to persist unsupported facets`, error);
    }
  }

  selectedFacetFilter(excludeAttribute = null) {
    return Array.from(this.selectedFacetValues.entries())
      .filter(([attribute, values]) => attribute !== excludeAttribute && values.size > 0)
      .map(([attribute, values]) => {
        const valueFilters = Array.from(values).map((value) => equalsFilter(attribute, value));
        return `(${valueFilters.join(" OR ")})`;
      })
      .join(" AND ");
  }

  helperRefinementFilter(excludeAttribute = null) {
    const state = this.search?.helper?.state;
    if (!state) return "";

    const groups = [];
    const addGroup = (attribute, values) => {
      if (
        attribute === excludeAttribute ||
        this.selectedFacetValues.has(attribute) ||
        !Array.isArray(values) ||
        values.length === 0
      ) {
        return;
      }

      groups.push(
        `(${values
          .map((value) => equalsFilter(attribute, value, { numeric: attribute === "vehicle_ids" }))
          .join(" OR ")})`,
      );
    };

    Object.entries(state.facetsRefinements || {}).forEach(([attribute, values]) => {
      addGroup(attribute, values);
    });

    Object.entries(state.disjunctiveFacetsRefinements || {}).forEach(([attribute, values]) => {
      addGroup(attribute, values);
    });

    return groups.join(" AND ");
  }

  selectedVehicleYearFilter() {
    if (
      !this.selectedYear ||
      !Array.isArray(this.activeVehicleIds) ||
      this.activeVehicleIds.length === 0
    ) {
      return "";
    }

    const maxIndexedYear = Math.max(this.selectedYear, new Date().getFullYear() + 1);
    const filters = [];

    for (let year = this.selectedYear; year <= maxIndexedYear; year += 1) {
      this.activeVehicleIds.forEach((vehicleId) => {
        filters.push(equalsFilter(`vehicle_years.year_to.${year}`, vehicleId));
      });
    }

    return filters.length > 0 ? `(${filters.join(" OR ")})` : "";
  }

  buildSearchFilters(excludeAttribute = null, { includeHelperRefinements = false } = {}) {
    return [
      this.baseFilters,
      this.manualProductIdFilterEnabled && this.manualProductIdsFilter
        ? `(${this.manualProductIdsFilter})`
        : "",
      this.selectedVehicleYearFilter(),
      this.selectedFacetFilter(excludeAttribute),
      includeHelperRefinements ? this.helperRefinementFilter(excludeAttribute) : "",
    ]
      .map((filter) => String(filter || "").trim())
      .filter(Boolean)
      .join(" AND ");
  }

  currentConfigureParams() {
    return {
      ...this.configureWidgetParams,
      filters: this.buildSearchFilters(),
    };
  }

  isUnsupportedManualProductIdFilterError(error) {
    return String(error?.message || error || "").includes("Attribute `objectID` is not filterable");
  }

  requestsWithCurrentFilters(requests) {
    if (!Array.isArray(requests)) return requests;

    const filters = this.buildSearchFilters();
    return requests.map((request) => {
      if (!request?.params) return request;

      return {
        ...request,
        params: {
          ...request.params,
          filters,
        },
      };
    });
  }

  updateConfigureWidget() {
    if (!this.search || !this.configureWidget) return;

    this.search.removeWidgets([this.configureWidget]);
    this.configureWidget = instantsearch.widgets.configure(this.currentConfigureParams());
    this.search.addWidgets([this.configureWidget]);
  }

  renderSortControls() {
    const sortByContainer = this.querySelector("#sort-by");
    if (!sortByContainer) return;

    const options = [
      { label: "Relevance", value: "" },
      { label: "Best Selling", value: "quantity_sold:desc" },
      { label: "Price (High to Low)", value: "price_from:desc" },
      { label: "Price (Low to High)", value: "price_from:asc" },
      { label: "Alphabetical (A-Z)", value: "title:asc" },
      { label: "Alphabetical (Z-A)", value: "title:desc" },
    ];

    sortByContainer.innerHTML = `
      <div class="w-full md:w-auto">
        <select class="rounded-md border text-xs text-gray-500 border-gray-300 focus:ring-red-500 focus:border-red-500 w-full" aria-label="Sort products">
          ${options
            .map(
              (option) => `
                <option value="${option.value}" ${option.value === this.currentSortValue ? "selected" : ""}>
                  ${option.label}
                </option>
              `,
            )
            .join("")}
        </select>
      </div>
    `;

    const select = sortByContainer.querySelector("select");
    select?.addEventListener("change", (event) => {
      this.currentSortValue = event.target.value;
      const nextIndexName = this.currentSortValue
        ? `${this.searchIndexName}:${this.currentSortValue}`
        : this.searchIndexName;

      this.showSearchLoadingIndicator();
      this.search?.helper?.setIndex?.(nextIndexName);
      this.search?.helper?.setPage?.(0);
      this.search?.helper?.search?.();
    });
  }

  hasSelectedFacetValues() {
    return Array.from(this.selectedFacetValues.values()).some((values) => values.size > 0);
  }

  setFacetSelection(attribute, value, selected) {
    const selectedValues = this.selectedFacetValues.get(attribute) || new Set();

    if (selected) {
      selectedValues.add(value);
      this.selectedFacetValues.set(attribute, selectedValues);
    } else {
      selectedValues.delete(value);
      if (selectedValues.size > 0) {
        this.selectedFacetValues.set(attribute, selectedValues);
      } else {
        this.selectedFacetValues.delete(attribute);
      }
    }

    this.renderFacetOptions(attribute);
    this.renderClearFiltersButton();
    this.updateConfigureWidget();
    this.scheduleFacetOptionSync();
  }

  clearFacetSelections() {
    if (!this.hasSelectedFacetValues()) return;

    this.selectedFacetValues.clear();
    this.facetOptionState.forEach((_, attribute) => this.renderFacetOptions(attribute));
    this.renderClearFiltersButton();
    this.updateConfigureWidget();
    this.scheduleFacetOptionSync();
  }

  mergeFacetOptions(attribute, facetData, { replace = false } = {}) {
    const options = replace
      ? new Map()
      : new Map(Array.from(this.facetOptionState.get(attribute) || new Map()));

    Object.entries(facetData || {}).forEach(([value, count]) => {
      options.set(value, Number(count) || 0);
    });

    const selectedValues = this.selectedFacetValues.get(attribute);
    if (selectedValues) {
      selectedValues.forEach((value) => {
        if (!options.has(value)) options.set(value, 0);
      });
    }

    this.facetOptionState.set(attribute, options);
  }

  facetOptionsFromResults(results, attribute) {
    if (!results?.getFacetByName) return {};

    try {
      return results.getFacetByName(attribute)?.data || {};
    } catch (error) {
      return {};
    }
  }

  renderFacetOptions(attribute) {
    const containerSelector = this.facetContainersByAttribute.get(attribute);
    const container = containerSelector ? this.querySelector(containerSelector) : null;
    if (!container) return;

    const selectedValues = this.selectedFacetValues.get(attribute) || new Set();
    const options = this.facetOptionState.get(attribute) || new Map();
    const items = Array.from(options.entries())
      .map(([value, count]) => ({
        value,
        label: value === "true" ? "Yes" : value === "false" ? "No" : value,
        count,
        isRefined: selectedValues.has(value),
      }))
      .sort(
        (a, b) =>
          Number(b.isRefined) - Number(a.isRefined) ||
          a.label.localeCompare(b.label, undefined, { numeric: true }) ||
          b.count - a.count,
      );

    container.innerHTML = `
      <div class="partbot-filter-values partbot-filter-list">
        ${items
          .map(
            ({ value, label, count, isRefined }) => `
              <div class="partbot-filter" data-value="${encodeURIComponent(value)}">
                <label>
                  <input
                    type="checkbox"
                    ${isRefined ? "checked" : ""}
                    class="accent-red-500 h-4 w-4 rounded border-gray-400 text-red-600 focus:ring-red-600"
                  />
                  <span class="filter-value">${escapeHtml(label)}</span>
                  <span class="filter-count">(${count})</span>
                </label>
              </div>
            `,
          )
          .join("")}
      </div>
    `;
  }

  renderClearFiltersButton() {
    const container = document.querySelector("#partbot-clear-filters");
    if (!container) return;

    const disabled = !this.hasSelectedFacetValues();
    container.innerHTML = `
      <button type="button" class="btn btn-sm btn-secondary disabled:hidden" ${
        disabled ? "disabled" : ""
      }>
        <span>Clear All</span>
      </button>
    `;

    const button = container.querySelector("button");
    if (button) {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.clearFacetSelections();
      });
    }
  }

  showSearchLoadingIndicator() {
    const status = this.querySelector("#partbot-search-status");
    if (!status) return;

    status.innerHTML = `
      <span class="inline-flex items-center gap-2">
        <span>Loading</span>
        <span
          aria-hidden="true"
          class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
        ></span>
      </span>
    `;
  }

  renderSearchStatus(count = null) {
    const status = this.querySelector("#partbot-search-status");
    if (!status) return;

    const productCount = Number(count);
    if (!this.hasReceivedSearchResults || !Number.isFinite(productCount)) {
      this.showSearchLoadingIndicator();
      return;
    }

    status.textContent = `Showing ${productCount} ${productCount === 1 ? "product" : "products"}`;
  }

  scheduleFacetOptionSync() {
    if (!this.searchKey || !this.searchHost || !this.searchIndexName) return;

    clearTimeout(this.facetSyncTimer);
    this.facetSyncTimer = setTimeout(() => {
      this.syncFacetOptionCounts().catch((error) => {
        console.warn(`${this.debugPrefix} failed to sync facet option counts`, error);
      });
    }, 100);
  }

  async syncFacetOptionCounts() {
    const attributes = Array.from(this.facetContainersByAttribute.keys());
    if (attributes.length === 0) return;

    const token = ++this.facetSyncToken;
    const query = this.search?.helper?.state?.query || "";
    const queries = attributes.map((attribute) => ({
      indexUid: this.searchIndexName,
      q: query,
      limit: 0,
      facets: [attribute],
      filter: this.buildSearchFilters(attribute, { includeHelperRefinements: true }),
    }));

    const response = await fetch(`${this.searchHost}/multi-search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.searchKey}`,
      },
      body: JSON.stringify({ queries }),
    });

    if (!response.ok) {
      throw new Error(`Facet option sync failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (token !== this.facetSyncToken) return;

    (data.results || []).forEach((result, index) => {
      const attribute = attributes[index];
      this.mergeFacetOptions(attribute, result.facetDistribution?.[attribute] || {}, {
        replace: true,
      });
      this.renderFacetOptions(attribute);
    });
  }

  async initializeSearch() {
    if (!this.apiKey) {
      throw new Error("Missing data-api-key on vehicle-search or partbot-collection-search");
    }

    if (!window.instantsearch) {
      throw new Error("instantsearch.js must be loaded before PartbotCollectionSearch");
    }

    await loadScriptOnce(
      "https://cdn.jsdelivr.net/npm/@meilisearch/instant-meilisearch/dist/instant-meilisearch.umd.min.js",
      "instantMeiliSearch",
    );

    const auth = await fetchSearchConfig(this.apiHost, this.apiKey, this.store);
    this.searchHost = normalizeSearchHost(auth.host, this.searchHost);
    this.searchKey = auth.store_search_key || auth.search_key;
    this.searchIndexName = auth.store_index_name || this.getAttribute("data-algolia-index");


    if (!this.searchKey || !this.searchIndexName) {
      throw new Error("Vehicle auth did not return store_search_key/search_key and store_index_name");
    }

    this.loadUnsupportedFacetAttributes();
    const persistedRemovedFacets = this.removeUnsupportedFacets(
      Array.from(this.unsupportedFacetAttributes),
    );
    if (persistedRemovedFacets.length > 0) {
    }

    const { searchClient } = window.instantMeiliSearch(this.searchHost, this.searchKey);
    const originalSearch = searchClient.search.bind(searchClient);
    searchClient.search = async (requests) => {
      const sanitizedRequests = this.sanitizeInstantSearchRequests(requests);


      try {
        const response = await originalSearch(sanitizedRequests);
        return response;
      } catch (error) {
        const unsupportedFacetAttributes = this.extractUnsupportedFacetAttributes(error);
        const removedFacets = this.removeUnsupportedFacets(unsupportedFacetAttributes);

        if (removedFacets.length > 0) {
          this.persistUnsupportedFacetAttributes();
          const retriedRequests = this.sanitizeInstantSearchRequests(requests);

          console.warn(`${this.debugPrefix} dropped unsupported facets and retrying`, {
            unsupportedFacetAttributes,
            removedFacets,
          });

          const response = await originalSearch(retriedRequests);
          return response;
        }

        if (isUnsupportedSortError(error)) {
          const retriedRequests = sortedRequestsOnPrimaryIndex(sanitizedRequests, this.searchIndexName, {
            dropSort: true,
          });

          console.warn(`${this.debugPrefix} dropped unsupported sort and retrying`, {
            indexName: this.searchIndexName,
            error,
          });

          const response = await originalSearch(retriedRequests);
          return response;
        }

        if (this.manualProductIdFilterEnabled && this.isUnsupportedManualProductIdFilterError(error)) {
          this.manualProductIdFilterEnabled = false;
          this.updateConfigureWidget();
          const retriedRequests = this.requestsWithCurrentFilters(sanitizedRequests);

          console.warn(`${this.debugPrefix} disabled manual objectID filter and retrying`, {
            collection: this.collection,
            collectionType: this.collectionType,
          });

          const response = await originalSearch(retriedRequests);
          return response;
        }

        console.error(`${this.debugPrefix} search error`, {
          indexName: this.searchIndexName,
          requests: sanitizedRequests,
          error,
        });
        throw error;
      }
    };

    this.search = instantsearch({
      indexName: resolveInitialSortIndex(
        this.searchIndexName,
        new URLSearchParams(window.location.search).get("sort_by") || this.defaultSortBy,
      ),
      searchClient,
      routing: false,
      searchFunction: (helper) => {
        if (this.vehicle_ids.length > 0) {
          this.vehicle_ids.map((id) => {
            helper.addDisjunctiveFacetRefinement("vehicle_ids", id);
          });
          this.vehicle_ids = [];
        }
        helper.search();
      },
      onStateChange: ({ uiState, setUiState }) => {
        this.showHitsContainerForVehicleUrl();

        setUiState(uiState);
      },
    });

    let vehicleIdFilter = "";
    if (this.showVehicleSearch && this.platesearch_ids.length > 0) {
      let vehicle_id_filters = this.platesearch_ids
        .map((vehicle_id) => equalsFilter("vehicle_ids", vehicle_id, { numeric: true }))
        .join(" OR ");
      vehicleIdFilter = `${vehicle_id_filters}`;
    }

    let filters = "";
    if (this.brands.length > 0) {
      let brand_filters = this.brands
        .map((brand) => equalsFilter("brand", brand))
        .join(` ${this.disjunctive} `);
      filters = `(${brand_filters})`;
    }

    if (this.product_types.length > 0) {
      let product_type_filters = this.product_types
        .map((product_type) => {
          // Decode Unicode escape sequences and then escape single quotes
          const decodedType = product_type
            .replace(/\\u[\dA-F]{4}/gi, (match) =>
              String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16)),
            )
          return equalsFilter("product_type", decodedType);
        })
        .join(` ${this.disjunctive} `);
      filters = filters
        ? `${filters} ${this.disjunctive} (${product_type_filters})`
        : `(${product_type_filters})`;
    }

    const configuredFilters = normalizeLegacySearchFilters(this.filters);
    if (configuredFilters) {
      filters = filters ? `(${filters}) AND (${configuredFilters})` : `(${configuredFilters})`;
    }

    if (vehicleIdFilter && vehicleIdFilter.length > 0) {
      filters = filters ? `(${filters}) AND (${vehicleIdFilter})` : `(${vehicleIdFilter})`;
    }

    if (this.tags.length > 0) {
      let tag_filters = this.tags
        .map((tag) => equalsFilter("tags", tag))
        .join(` ${this.disjunctive} `);
      filters = filters
        ? `(${filters}) ${this.disjunctive} (${tag_filters})`
        : `(${tag_filters})`;
    }

    // add published:true to filters
    filters = filters ? `(${filters}) AND published = true` : `published = true`;
    filters = `(${filters}) AND saleable = true`;

    // filter out listing_status:invalid
    filters = `(${filters}) AND listing_status != ${filterValue("invalid")}`;

    // if collectionType is Manual, and productIds exists, add productIds to objectId filter
    if (this.collectionType === "Manual" && this.productIds) {
      let productIds = this.productIds
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
      this.manualProductIdsFilter = productIds
        .map((id) => equalsFilter("objectID", id, { numeric: true }))
        .join(" OR ");

    }

    const configuredFacetAttributes = uniq(
      this.facets.map((facet) => normalizeFacetAttribute(facet)),
    );


    this.baseFilters = filters;
    this.configureWidgetParams = {
      hitsPerPage: this.facets && this.facets.length ? 15 : 18,
      distinct: true,
      maxValuesPerFacet: 100,
      facetingAfterDistinct: true,
      facets: uniq([
        "vehicle_years.year_from",
        "vehicle_years.year_to",
        "vehicle_makes",
      ].concat(configuredFacetAttributes)),
      disjunctiveFacets: uniq([
        "vehicle_years.year_from",
        "vehicle_years.year_to",
        "vehicle_ids",
        "vehicle_makes",
      ].concat(configuredFacetAttributes)),
    };
    this.configureWidget = instantsearch.widgets.configure(this.currentConfigureParams());
    this.search.addWidgets([this.configureWidget]);

    // Add Facet Filters
    if (this.facets && this.facets.length) {
      this.facets.forEach((facet) => {
        let node = document.createElement("div");
        node.classList.add("partbot-filter-container", "group", "expanded");
        let facet_id = facet.replace(/[\s_()]+/g, "-").toLowerCase();
        node.innerHTML = `<div class="partbot-filter-title">${facet
          .replace(/_/g, " ")
          .replace("vendor", "brand")}</div><div id='partbot-filter-${facet_id}'></div>`;

        let attr_title = normalizeFacetAttribute(facet);

        this.facetLabelsByAttribute.set(attr_title, facet);
        this.facetContainersByAttribute.set(attr_title, `#partbot-filter-${facet_id}`);
        this.facetOptionState.set(attr_title, new Map());
        this.facetFiltersContainer.appendChild(node);

        const container = this.querySelector(`#partbot-filter-${facet_id}`);
        if (container) {
          container.addEventListener("change", (event) => {
            if (!event.target.matches('input[type="checkbox"]')) return;

            const itemElement = event.target.closest(".partbot-filter");
            const value = decodeURIComponent(itemElement.getAttribute("data-value") || "");
            if (!value) return;

            this.setFacetSelection(attr_title, value, event.target.checked);
          });
        }

        this.search.addWidgets([
          {
            render: ({ results }) => {
              this.mergeFacetOptions(attr_title, this.facetOptionsFromResults(results, attr_title));
              this.renderFacetOptions(attr_title);
            },
          },
        ]);
      });
    }

    // add clear filters button
    this.renderClearFiltersButton();

    this.renderSortControls();

    this.search.addWidget(
      instantsearch.widgets.pagination({
        container: "#partbot-pagination",
        autoHideContainer: true,
        padding: 1,
        scrollTo: "#partbot-product-grid",
        templates: {
          first: "<<",
          previous: "<",
          next: ">",
          last: ">>",
        },
        cssClasses: {
          list: "partbot-pagination-list",
          selectedItem: "active",
          link: "partbot-pagination-button",
          disabledItem: "disabled hidden",
          firstPageItem: "partbot-pagination-control",
          lastPageItem: "partbot-pagination-control",
          nextPageItem: "partbot-pagination-control",
          previousPageItem: "partbot-pagination-control",
        },
      }),
    );

    // Add Hits Widget
    const filterM = this.filterAlgoliaHits;
    let _this = this;
    this.search.addWidget(
      instantsearch.widgets.hits({
        container: "#partbot-product-grid",
        cssClasses: {
          list: `partbot-product-grid-list ${
            this.facets && this.facets.length ? "has-filters" : ""
          }`,
        },
        templates: {
          item(hit, { html, components, sendEvent }) {
            return html`<div class="product-card-placeholder" data-handle="${hit.handle}"></div>`;
          },
          empty(results, { html }) {
            return html`<div class="empty">
              <div class="max-w-7xl p-12 lg:py-20">
                <h2 class="mb-4 text-4xl font-bold tracking-tight text-gray-900">
                  No results found.
                </h2>
                <p class="prose text-lg text-gray-500">
                  Try resetting your vehicle selection or choosing different filters.
                </p>
              </div>
            </div>`;
          },
        },
        transformItems(items) {
          _this.skus = [];
          return items.filter(filterM, this).filter((i) => i);
        },
      }),
    );

    this.pendingCardLoad = null; // Add this to track pending loads

    this.search.on("render", () => {
      this.scheduleFacetOptionSync();
      const results = this.search.helper?.lastResults;
      if (results) {
        this.hasReceivedSearchResults = true;
        this.lastResultsCount =
          typeof results.nbHits === "number" ? results.nbHits : results.hits?.length;
      }

      // Cancel any pending card load
      if (this.pendingCardLoad) {
        clearTimeout(this.pendingCardLoad);
      }

      // Delay the card load slightly to ensure we don't have multiple loads
      this.pendingCardLoad = setTimeout(() => {
        window.scrollTo(0, 0);
        this.showSearchLoadingIndicator();
        loadProductCards(this.collection).finally(() => {
          this.renderSearchStatus(this.lastResultsCount);
        });
        this.pendingCardLoad = null;
      }, 100);
    });

    this.search.start();
    this.showSearchLoadingIndicator();
  }

  cacheVehicleSearchSelection(detail = {}) {
    this.vehicle_ids = Array.isArray(detail.vehicle_ids) ? detail.vehicle_ids : [];
    this.activeVehicleIds = this.vehicle_ids;

    if (detail.vehicle?.year?.value) {
      this.selectedYear = parseInt(detail.vehicle.year.value, 10);
    } else {
      this.selectedYear = null;
    }

    this.showHitsContainerForVehicleUrl();
  }

  showHitsContainerForVehicleUrl() {
    if (
      this.hitsContainer &&
      this.activeVehicleIds.length > 0 &&
      window.location.search.includes("make=")
    ) {
      this.hitsContainer.style.display = "block";
    }
  }

  processHits(item) {
    if (this.selectedYear) {
      // collect all year_froms <= selectedYear and year_tos >= selectedYear
      var yf_vehicle_ids = [];
      var yt_vehicle_ids = [];
      var yf_permitted, yt_permitted;
      if (
        item?.vehicle_years?.year_from !== undefined &&
        item?.vehicle_years?.year_to !== undefined
      ) {
        // collect year_froms
        for (var year in item.vehicle_years.year_from) {
          if (year <= this.selectedYear) {
            yf_vehicle_ids.push(item.vehicle_years.year_from[year]);
          }
        }

        // collect year_tos
        for (var year in item.vehicle_years.year_to) {
          if (year >= this.selectedYear) {
            yt_vehicle_ids.push(item.vehicle_years.year_to[year]);
          }
        }

        yf_vehicle_ids = yf_vehicle_ids.flat();
        yt_vehicle_ids = yt_vehicle_ids.flat();

        yf_permitted = this.activeVehicleIds.filter((n) => yf_vehicle_ids.includes(n));

        yt_permitted = this.activeVehicleIds.filter((n) => yt_vehicle_ids.includes(n));

        // remove from results if not permitted
        if (yf_permitted.length === 0 || yt_permitted.length === 0) {
          return false;
        }
      }
    }

    // truncate item.title to 140 characters
    if (item.title.length > 140) {
      item.title = item.title.substring(0, 140) + "...";
    }

    // is multi_variant?
    item.multi_variant = item.variant_skus && item.variant_skus.length > 1;

    // check if the item has a compare_at_price_from value and if it is less than or equal to the price_from value set it to null
    if (item.compare_at_price_from && item.compare_at_price_from <= item.price_from) {
      item.compare_at_price_from = null;
    }

    return item;
  }

  applyVehicleSearchSelection(e) {
    if (e && this.lastVehicleSearchEvent === e) return false;
    this.lastVehicleSearchEvent = e;

    const detail = e.detail || {};

    // if (this.platesearch_ids && this.platesearch_ids.length) {
    //   return false;
    // }

    this.cacheVehicleSearchSelection(detail);

    if (!this.search?.helper) {
      return false;
    }

    this.updateConfigureWidget();

    this.vehicle_ids = [];
    this.search.helper.clearRefinements("vehicle_ids");
    this.search.helper.clearRefinements("vehicle_makes");

    if (detail.vehicle?.make) {
      this.search.helper.addFacetRefinement("vehicle_makes", detail.vehicle.make.value);
    }

    if (this.activeVehicleIds.length > 0) {
      this.activeVehicleIds.map((id) => {
        this.search.helper.addDisjunctiveFacetRefinement("vehicle_ids", id);
      });
    }

    this.search.helper.search();
  }
}

function fetchProductCards(handles, collection, aspectRatio = "1.0") {
  if (handles.length === 0) {
    return Promise.resolve([]);
  }

  const cacheBuster = new Date().getTime();
  const seedHandles = handles.filter(Boolean);
  const parser = new DOMParser();

  const fetchBatch = (seedHandle) => {
    const url = `products/${seedHandle}?view=partbot-product-cards&c=${collection}&ar=${aspectRatio}${handles
      .filter((handle) => handle && handle !== seedHandle)
      .map((handle) => `&h=${encodeURIComponent(handle)}`)
      .join("")}&cache=false&type=${cacheBuster}`;

    return fetch(window.Shopify.routes.root + url, {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    });
  };

  const parseCards = (html) => {
    const doc = parser.parseFromString(html, "text/html");
    const productCards = doc.querySelectorAll(".product-card");
    const cardMap = new Map();

    productCards.forEach((card) => {
      const handle = card.dataset.handle;
      cardMap.set(handle, card.outerHTML);
    });

    return handles
      .map((handle) => cardMap.get(handle))
      .filter((card) => card !== undefined);
  };

  const trySeedHandle = (index = 0, lastError = null) => {
    if (index >= seedHandles.length) {
      if (lastError) throw lastError;
      return [];
    }

    return fetchBatch(seedHandles[index])
      .then(parseCards)
      .then((cards) => {
        if (cards.length > 0) return cards;
        return trySeedHandle(
          index + 1,
          new Error("No product cards returned from partbot-product-cards template"),
        );
      })
      .catch((error) => trySeedHandle(index + 1, error));
  };

  return trySeedHandle();
}

function loadProductCards(collection, aspectRatio) {
  const existingIndicators = document.querySelectorAll("loading-indicator");
  existingIndicators.forEach((indicator) => indicator.remove());

  const loadingIndicator = document.createElement("loading-indicator");
  const container = document.querySelector("#partbot-product-grid");

  if (!container) return Promise.resolve();

  container.parentElement.appendChild(loadingIndicator);

  const placeholders = Array.from(document.querySelectorAll(".product-card-placeholder"));

  const handles = placeholders.map((placeholder) => placeholder.dataset.handle).filter(Boolean);

  if (handles.length === 0) {
    loadingIndicator.remove();
    return Promise.resolve();
  }

  return fetchProductCards(handles, collection, aspectRatio)
    .then((cardHtmls) => {
      cardHtmls.forEach((cardHtml, index) => {
        const placeholder = placeholders[index];
        if (placeholder && placeholder.parentNode) {
          placeholder.outerHTML = cardHtml;
        }
      });
    })
    .finally(() => {
      loadingIndicator.remove();
    });
}

// move the loading indicator to a component

customElements.define("partbot-collection-search", PartbotCollectionSearch);
